import React, { useState, useEffect } from "react";
import {
    Grid,
    Typography,
    Switch,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    Box,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import AppDownloadInfo from "./appdownloadinfo";
import WebsiteSettings from "./websitesettings";
import { useGetSettingsQuery, useUpdateSettingsMutation } from "state/api";
import { useSnack, SaveSnackbar } from "./settingsHelper";

// Only render the live Google map when a real key is configured; otherwise the
// widget shows Google's grey "can't load Google Maps correctly" error overlay.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const HAS_MAPS_KEY = Boolean(GOOGLE_MAPS_API_KEY);

const General = () => {
    const { data } = useGetSettingsQuery();
    const settings = data?.data || {};
    const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
    const { snack, showSuccess, showError, closeSnack } = useSnack();

    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [country, setCountry] = useState("India");
    const [companyAddress, setCompanyAddress] = useState("");
    const [latitude, setLatitude] = useState(21.7679);
    const [longitude, setLongitude] = useState(78.8718);
    const [markerPosition, setMarkerPosition] = useState({ lat: 21.7679, lng: 78.8718 });

    // Load persisted business details into the form.
    useEffect(() => {
        const bd = settings.businessDetails || {};
        const addr = bd.address || {};
        const general = settings.custom?.general || {};
        if (bd.name !== undefined) setCompanyName(bd.name);
        if (bd.phone !== undefined) setPhone(bd.phone);
        if (bd.email !== undefined) setEmail(bd.email);
        if (addr.country !== undefined) setCountry(addr.country);
        if (addr.street !== undefined) setCompanyAddress(addr.street);
        if (general.maintenanceMode !== undefined) setMaintenanceMode(general.maintenanceMode);
        if (general.latitude !== undefined && general.longitude !== undefined) {
            setLatitude(general.latitude);
            setLongitude(general.longitude);
            setMarkerPosition({ lat: general.latitude, lng: general.longitude });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Function to fetch address from lat/lng
    const updateAddressFromLatLng = async (lat, lng) => {
        setLatitude(lat);
        setLongitude(lng);
        setMarkerPosition({ lat, lng });

        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const geoData = await response.json();

            if (geoData.status === "OK" && geoData.results.length > 0) {
                setCompanyAddress(geoData.results[0].formatted_address);
            } else {
                console.error("Geocoding API returned no results:", geoData);
            }
        } catch (error) {
            console.error("Error fetching address:", error);
        }
    };

    const handleSave = async () => {
        try {
            await updateSettings({
                businessDetails: {
                    name: companyName,
                    phone,
                    email,
                    address: { country, street: companyAddress },
                },
                custom: { general: { maintenanceMode, latitude, longitude } },
            }).unwrap();
            showSuccess();
        } catch (err) {
            showError(err?.data?.error?.message);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <CardContent>
                        <Typography variant="h6" color="text.primary">System Maintenance</Typography>
                        <Typography variant="body2" color="text.secondary">
                            By turning on maintenance mode, Control your all system & function.
                        </Typography>
                        <Switch checked={maintenanceMode} onChange={(e) => setMaintenanceMode(e.target.checked)} />
                        <Typography variant="h6" color="text.primary" sx={{ mt: 4, mb: 4 }}>
                            Company Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="country-select-label">Country</InputLabel>
                                    <Select labelId="country-select-label" id="country-select" value={country} label="Country" onChange={(e) => setCountry(e.target.value)}>
                                        <MenuItem value="India">India</MenuItem>
                                        <MenuItem value="United States">United States</MenuItem>
                                        <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                        <MenuItem value="United Arab Emirates">United Arab Emirates</MenuItem>
                                        <MenuItem value="Singapore">Singapore</MenuItem>
                                        <MenuItem value="Pakistan">Pakistan</MenuItem>
                                        <MenuItem value="Nepal">Nepal</MenuItem>
                                        <MenuItem value="Bangladesh">Bangladesh</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Company Address" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField fullWidth label="Latitude" value={latitude} onChange={(e) => setLatitude(parseFloat(e.target.value))} />
                                    <LocationOnIcon sx={{ color: "primary.main", ml: 1 }} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField fullWidth label="Longitude" value={longitude} onChange={(e) => setLongitude(parseFloat(e.target.value))} />
                                    <LocationOnIcon sx={{ color: "primary.main", ml: 1 }} />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
                                    {isLoading ? "Saving..." : "Save Company Information"}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* Google Map Section — only when a real API key is configured */}
            {HAS_MAPS_KEY ? (
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <CardContent>
                            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                                <GoogleMap
                                    center={markerPosition}
                                    zoom={10}
                                    mapContainerStyle={{ height: "300px", width: "100%", borderRadius: 8 }}
                                    onClick={(e) => updateAddressFromLatLng(e.latLng.lat(), e.latLng.lng())}
                                >
                                    {markerPosition && (
                                        <Marker
                                            position={markerPosition}
                                            draggable
                                            onDragEnd={(e) => updateAddressFromLatLng(e.latLng.lat(), e.latLng.lng())}
                                        />
                                    )}
                                </GoogleMap>
                            </LoadScript>
                        </CardContent>
                    </Card>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <CardContent
                            sx={{
                                height: 200,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                textAlign: 'center',
                            }}
                        >
                            <LocationOnIcon sx={{ color: 'text.disabled', fontSize: 36 }} />
                            <Typography variant="body2" color="text.secondary">
                                Map preview is disabled. Set the location using the Latitude / Longitude
                                fields above.
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                Add a Google Maps API key (REACT_APP_GOOGLE_MAPS_API_KEY) to enable the
                                interactive map.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            <Grid item xs={12}>
                <AppDownloadInfo />
            </Grid>
            <Grid item xs={12}>
                <WebsiteSettings />
            </Grid>

            <SaveSnackbar snack={snack} onClose={closeSnack} />
        </Grid>
    );
};

export default General;
